import { AddIcon, Dropdown, ThemePrepared } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import defaultStyle from '../../../components/shared/components/tree/DraggableItem/DraggableItem.module.css';
import '../../shared/components/Wysiwyg/HelpCardEditor.css';
import AppCSS from '../../App.module.css';
import { appState } from '../../AppState';
import { ThemeColorScheme } from '../../shared/common/TeamsTheme';
import { ADMINISTRATORS, AGENTS, ANALYSTS, CustomViewType, TREE_LISTING_CONSTANTS } from '../../shared/utils/constants';
import { listInputItems } from '../ticketHelper';
import { CustomViewContext, DEFAULT_SORT_ORDER } from '../toolbar/CustomViewContextProvider';
import ToolbarCSS from '../toolbar/Toolbar.module.css';
import useComponentVisible from '../toolbar/useComponentVisible';
import useWindowWidth from '../useWindowWidth';

const DropdownTitle = ({ children }) => {
  return (
    <div className='py-1' role='none'>
      <div
        className={`block px-4 py-2 text-sm cursor-default ${ToolbarCSS.parentDropdownTitle}`}
      >
        {children}
      </div>
    </div>
  );
};

const MENU_ITEM_SELECTOR = '[role="menuitem"]';

// A long view name is cut with an ellipsis rather than stretching the toolbar or wrapping in the menu.
const TRUNCATED_TEXT: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
const TRIGGER_MAX_WIDTH = 240;

// The full name shows as a tooltip on hover, but only when it has actually been cut.
const showTitleWhenTruncated = (fullText: string) => (event: React.MouseEvent<HTMLElement>) => {
  const text = event.currentTarget.querySelector<HTMLElement>('[data-truncated-text]');
  event.currentTarget.title = text && text.scrollWidth > text.clientWidth ? fullText : '';
};
interface ICustomViewsDropdown {
    onClickDefaultView?: (id: number) => void
    viewName:string;
    sortedViews:any[];
    showAddView:boolean;
  globalTheme: ThemePrepared<any>;
  isCustomViewEnabled: boolean;
  isScopingEnabled: boolean;
  customCssClass?:string;
  fitContainer?: boolean;
  selectView: (customView: CreateCustomView, defaultView: CreateCustomView) => void
}

export const CustomViewsDropdown = ({ globalTheme, isCustomViewEnabled, selectView, viewName, sortedViews, showAddView, onClickDefaultView, isScopingEnabled, customCssClass, fitContainer = false }: ICustomViewsDropdown) => {
  const currentState = appState();
  const {t} = useTranslation();

  const { setCustomViewStates } = useContext(CustomViewContext);
  const [showDropdown, setShowDropdown] = useState<number | boolean>(false);
  const defaultView = sortedViews.find(x => x.Type == CustomViewType.DefaultView) ?? null;

  const { parentRef, ref } = useComponentVisible(setShowDropdown);

  const isDarkMode = globalTheme.siteVariables.theme === themeNames.Dark;
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 767;
  const fitsContainer = fitContainer && !isMobile;

  const getVisibleMenuItems = (): HTMLElement[] =>
    Array.from((ref.current as HTMLElement | null)?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? []).filter(el => el.offsetParent !== null);

  // Toggle on pointerdown rather than click: iOS only synthesizes a click after the tap has ended,
  // and drops it whenever that same tap already changed the DOM (here, the hover state clearing off
  // the trigger). That left the dropdown impossible to reopen once a view had been picked.
  const onTriggerPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    setShowDropdown(prev => !prev);
  };

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
        event.preventDefault();
        setShowDropdown(true);
        break;
      case 'Escape':
        if (showDropdown) {
          event.preventDefault();
          setShowDropdown(false);
        }
        break;
      default:
        break;
    }
  };

  const focusMenuItemAt = (menuItems: HTMLElement[], index: number) => {
    if (menuItems.length === 0) return;
    menuItems[(index + menuItems.length) % menuItems.length]?.focus();
  };

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const menuItems = getVisibleMenuItems();
    const currentIndex = menuItems.indexOf(document.activeElement as HTMLElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusMenuItemAt(menuItems, currentIndex === -1 ? 0 : currentIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusMenuItemAt(menuItems, currentIndex === -1 ? menuItems.length - 1 : currentIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusMenuItemAt(menuItems, 0);
        break;
      case 'End':
        event.preventDefault();
        focusMenuItemAt(menuItems, menuItems.length - 1);
        break;
      case 'Enter':
      case ' ':
        if (currentIndex !== -1) {
          event.preventDefault();
          (document.activeElement as HTMLElement).click();
          parentRef.current?.focus();
        }
        break;
      case 'Escape':
        event.preventDefault();
        setShowDropdown(false);
        parentRef.current?.focus();
        break;
      default:
        break;
    }
  };

  // Never leave the menu flagged open after the selection has moved on, otherwise the next tap just
  // toggles invisible state and the dropdown looks stuck.
  useEffect(() => {
    setShowDropdown(false);
  }, [viewName]);

  // Move focus onto the first menu item as soon as the dropdown opens, so keyboard users land inside it.
  useEffect(() => {
    if (showDropdown) {
      getVisibleMenuItems()[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDropdown]);

  const onClickAddNewView = () => {
    setCustomViewStates((prev): CustomViewStatesType => ({
      ...prev,
      showCustomViewModal: !prev.showCustomViewModal,
      view: 'add',
      columns: {
        columnsKeyOrder: [],
        selectedColumns: [],
        sortOrder: DEFAULT_SORT_ORDER,
      }
    }));
  };

  let wrapperClassName = 'relative inline-block text-left';
  if (isMobile) {
    wrapperClassName = 'relative text-left';
  } else if (fitsContainer) {
    // Not positioned, so the menu anchors to the container and can be wider than a short trigger.
    wrapperClassName = 'inline-block text-left';
  }

  return (
      <div
        className={wrapperClassName}
        style={
          isMobile
            ? { backgroundColor: '--mgt-theme-background', display: 'block', width: '100%', minWidth: 0, ...ThemeColorScheme(globalTheme.siteVariables) }
            : { backgroundColor: '--mgt-theme-background', maxWidth: fitsContainer ? '100%' : TRIGGER_MAX_WIDTH, minWidth: 0, ...ThemeColorScheme(globalTheme.siteVariables) }
        }
      >
        <div
          className={`relative cursor-pointer ${ToolbarCSS.dropdownTrigger}`}
          id='parent'
          role='button'
          tabIndex={0}
          aria-haspopup='true'
          aria-expanded={!!showDropdown}
          aria-label={viewName}
          onPointerDown={onTriggerPointerDown}
          onKeyDown={onTriggerKeyDown}
          onMouseEnter={showTitleWhenTruncated(viewName)}
          ref={parentRef}
        >
          <Dropdown
            items={[]}
            triggerButton={{
              content: (
                <span data-truncated-text style={{ display: 'block', ...TRUNCATED_TEXT }}>
                  {viewName}
                </span>
              ),
              styles: {
                overflow: 'hidden',
              },
            }}
            fluid
            className={`${AppCSS.dropdownItem} ${ToolbarCSS.hideItems} ${ToolbarCSS.dropdownTriggerPassthrough} dropdown-btn`}
          />
        </div>

        {showDropdown && (
          <div
            className={`${fitsContainer ? 'right-0 w-60 max-w-full' : 'origin-top-left right-0 md:origin-top-right md:left-0 w-60'} absolute rounded-md ring-1 ring-black ring-opacity-5 divide-y focus:outline-none max-h-3/4 overflow-y-scroll 
            ${ToolbarCSS.secondaryToolbarBg} ${isDarkMode && 'divide-gray-500'} ${customCssClass}`}
            // max-h-3/4 is in vh, which iOS Safari measures with its toolbars hidden, so the end of a long
            // list sat under the bottom toolbar (#35363). dvh follows what is visible; without dvh support
            // the value is dropped and the class still applies.
            style={{ zIndex: 9, maxHeight: 'calc(75dvh - 60px)' }}
            role='menu'
            aria-orientation='vertical'
            aria-label={viewName}
            tabIndex={-1}
            ref={ref}
            onKeyDown={onMenuKeyDown}
          >
            {!isCustomViewEnabled && 
                <>
                <div className={``}><DropdownTitle>{t('ticket.ticket-list.configure-column.system-default')}</DropdownTitle></div>
                    <div className='py-1' role='none'>
                    {Object.entries(listInputItems).map(([key, item]) => (
                        <div
                        key={key}
                        className={`cursor-pointer block px-4 py-2 text-sm ${ToolbarCSS['custom-view-item']}`}
                        role='menuitem'
                        tabIndex={-1}
                        onClick={() => {
                            onClickDefaultView(Number(key))
                            setShowDropdown(false);
                        }}
                        >
                        {item}
                        </div>
                    ))}
                    </div>
                </>
            }
            {isCustomViewEnabled && <div className={`divide-y ${isDarkMode && 'divide-gray-500'}`}>
              {showAddView && (currentState?.userRoles?.roles?.includes(ADMINISTRATORS) || currentState?.userRoles?.roles?.includes(ANALYSTS) || currentState?.userRoles?.roles?.includes(AGENTS)) && <div role='none'>
                <div
                  className={`hidden md:flex justify-between items-center cursor-pointer px-4 py-2 text-sm ${ToolbarCSS['custom-view-add-btn']}`}
                  style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}
                  role='menuitem'
                  tabIndex={-1}
                  onClick={onClickAddNewView}
                >
                  {t('ticket.ticket-list.configure-column.add-new-view')}
                  <AddIcon styles={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }} />
                </div>
              </div>}
              <DropdownTitle>{t('ticket.ticket-list.configure-column.organizational-views')}</DropdownTitle>
              <div role='none'>                {sortedViews?.filter(x=> (x.Type==CustomViewType.ScopedView && x.ScopedMembers.length>0) || x.Type==CustomViewType.DefaultView || x.Type==CustomViewType.CompanyView).map((customView) => (
                  <div                    className={`flex items-center justify-between cursor-pointer px-4 py-2 text-sm ${ToolbarCSS['custom-view-item']}`}
                    style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}
                    role='menuitem'
                    tabIndex={-1}
                    key={customView.Id}
                    onMouseEnter={showTitleWhenTruncated(customView.Name)}
                    onClick={()=>{
                        selectView(customView,defaultView);
                        setShowDropdown(false);
                    }}
                  >
                    <span className='flex-1' data-truncated-text style={TRUNCATED_TEXT}>{customView.Name}</span>
                    {customView.Type==CustomViewType.DefaultView && (currentState?.userRoles?.roles?.includes(ADMINISTRATORS) || currentState?.userRoles?.roles?.includes(ANALYSTS)) &&
                      <span className={`${defaultStyle.Default} flex-shrink-0`} style={{ marginLeft: '4px' }}>{TREE_LISTING_CONSTANTS.default}</span>
                    }                    {isScopingEnabled && customView.ScopedMembers?.length>0 && (currentState?.userRoles?.roles?.includes(ADMINISTRATORS) || currentState?.userRoles?.roles?.includes(ANALYSTS)) &&
                      <span
                        className={`scopedCustomButton flex-shrink-0 ${isDarkMode ? "scopedCustomButtonDark" : "scopedCustomButtonLight"}`}
                        style={{ borderLeft: 'none', width: '18px', marginLeft: '4px' }}
                        title={t('scoping.header')}
                      ></span>
                    }
                  </div>
                ))}
              </div>
              {isScopingEnabled && 
              <>
                <DropdownTitle>{t('ticket.ticket-list.configure-column.custom-views')}</DropdownTitle>
                <div role='none'>                  {sortedViews?.filter(x=> x.Type==CustomViewType.MyView && x.ModifiedById==currentState.currentUserId).map((customView) => (
                    <div                      className={`flex items-center justify-between cursor-pointer px-4 py-2 text-sm ${ToolbarCSS['custom-view-item']}`}
                      style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}
                      role='menuitem'
                      tabIndex={-1}
                      key={customView.Id}
                      onMouseEnter={showTitleWhenTruncated(customView.Name)}
                      onClick={()=>{
                          selectView(customView,defaultView);
                          setShowDropdown(false);
                      }}
                    >
                      <span className='flex-1' data-truncated-text style={TRUNCATED_TEXT}>{customView.Name}</span>
                      {customView.ScopedMembers?.length>0 && (currentState?.userRoles?.roles?.includes(ADMINISTRATORS) || currentState?.userRoles?.roles?.includes(ANALYSTS)) &&
                        <span
                          className={`scopedCustomButton flex-shrink-0 ${isDarkMode ? "scopedCustomButtonDark" : "scopedCustomButtonLight"}`}
                          style={{ borderLeft: 'none', width: '18px', marginLeft: '4px' }}
                          title={t('scoping.header')}
                        ></span>
                      }
                    </div>
                  ))}
                </div>
              </>
              }
            </div>}
          </div>
        )}
      </div>
  );
};
